/**
 * Ability clips: a small data-driven format for procedural hero animation (pure, no three.js).
 *
 * A clip is a list of keyframed poses on the VRM normalized bones. Each key holds offsets that are
 * added on top of the base locomotion pose (breathing, run cycle, stun slump). Each key also sets
 * the easing of the segment that arrives at it and can hold its pose for a while. A clip can also
 * have:
 *  - root offsets: lunge, side step, hop and spin about Y. These are visual only. The unit's sim
 *    position never moves, and the offsets blend back to zero.
 *  - weapon-trail windows: [from, to) seconds, colour, width, hand and texture style. A ribbon
 *    follows the real blade tip while the window is open.
 *  - fx cue markers (time → effect id at the blade tip, hand, in front or on the ground) and
 *    hit-stop markers.
 *  - looping (channels): the clip plays once, then repeats [loopFrom, duration) until stopped.
 *  - a locomotion mask: 'auto' (the default) plays upper body only while running and full body
 *    when standing. 'upper' never touches the legs. 'full' always does.
 *
 * Authoring: write a `ClipDef` and pass it to `defineClip` once, at module load. Register the
 * result per ability (anim/registry.ts). See anim/classes/blademaster.ts for a fully commented
 * example.
 *
 * Runtime: `ClipPlayer` advances one clip, crossfades from whatever was playing, fires cues
 * through a `ClipSink`, opens and closes trail windows, and writes weighted, masked offsets into
 * a reused Float32Array. Nothing in `update` / `evalClip` allocates.
 *
 * ## Pose channels (radians; left/right are mirrored, so the same number means the same motion)
 *  torso   hips, spine, chest, neck, head    [pitch: bend forward +, twist: turn left +, lean: lean right +]
 *  arms    leftUpperArm, rightUpperArm       [raise forward/up +, sweep across the front +, raise sideways +]
 *          leftLowerArm, rightLowerArm       [elbow bend +, forearm twist, -]
 *          leftHand, rightHand               [roll (about the forearm), yaw, pitch]
 *  legs    leftUpperLeg, rightUpperLeg       [thigh forward +, twist, spread outward +]
 *          leftLowerLeg, rightLowerLeg       [knee bend +, -, -]
 *          leftFoot, rightFoot               [pitch, -, -]
 *  root    lunge (yd forward +), side (yd right +), hop (yd up +), spin (turns; + = counter-clockwise from above, i.e. turning left)
 *
 * The arms hang down by default (about 1.2 rad below the T-pose). "raise sideways 1.2" brings an
 * arm to shoulder height, and "raise forward 2.3" puts a sword overhead.
 */

export const CLIP_BONES = [
  'hips', 'spine', 'chest', 'neck', 'head',
  'leftUpperArm', 'rightUpperArm', 'leftLowerArm', 'rightLowerArm', 'leftHand', 'rightHand',
  'leftUpperLeg', 'rightUpperLeg', 'leftLowerLeg', 'rightLowerLeg', 'leftFoot', 'rightFoot',
] as const;
export type ClipBone = (typeof CLIP_BONES)[number];

export const BONE_INDEX = Object.fromEntries(CLIP_BONES.map((b, i) => [b, i])) as Record<ClipBone, number>;
/** Channel index of bone `b`, axis `a` (0..2). */
export const ch = (b: ClipBone, a: 0 | 1 | 2): number => BONE_INDEX[b] * 3 + a;
/** Root channels (after the 17 × 3 bone channels). */
export const CH_LUNGE = CLIP_BONES.length * 3;
export const CH_SIDE = CH_LUNGE + 1;
export const CH_HOP = CH_LUNGE + 2;
export const CH_SPIN = CH_LUNGE + 3;
/** Channels per pose. */
export const NCH = CH_SPIN + 1;
/** Hand roll channels (twirls), wrapped like the spin when a clip is cut. */
const CH_ROLL_R = ch('rightHand', 0);
const CH_ROLL_L = ch('leftHand', 0);

/** 1 for channels masked off while running ('auto') or always ('upper'): hips, legs, lunge/side/hop. */
export const LOWER_BODY = new Uint8Array(NCH);
for (const b of ['hips', 'leftUpperLeg', 'rightUpperLeg', 'leftLowerLeg', 'rightLowerLeg', 'leftFoot', 'rightFoot'] as const) {
  for (let a = 0; a < 3; a++) LOWER_BODY[BONE_INDEX[b] * 3 + a] = 1;
}
LOWER_BODY[CH_LUNGE] = LOWER_BODY[CH_SIDE] = LOWER_BODY[CH_HOP] = 1;

const LEFT_ARM: ClipBone[] = ['leftUpperArm', 'leftLowerArm', 'leftHand'];
const RIGHT_ARM: ClipBone[] = ['rightUpperArm', 'rightLowerArm', 'rightHand'];

// ---------------------------------------------------------------------------
// Easing
// ---------------------------------------------------------------------------

/**
 * Easing of the segment arriving at a key:
 *  linear      constant speed
 *  in          slow start, fast finish (cubic): wind-ups
 *  out         fast start, soft landing (cubic): recoveries
 *  inOut       smooth both ends
 *  snap        very fast start, settles hard (exponential): strikes
 *  back        overshoots the key, then settles (`overshoot` scales it): strikes with follow-through
 *  anticipate  pulls back first, then goes (`overshoot` scales the pull)
 *  step        holds the previous pose, then jumps at the key time
 */
export type Ease = 'linear' | 'in' | 'out' | 'inOut' | 'snap' | 'back' | 'anticipate' | 'step';
export const EASES: readonly Ease[] = ['linear', 'in', 'out', 'inOut', 'snap', 'back', 'anticipate', 'step'];
const EASE_ID: Record<Ease, number> = Object.fromEntries(EASES.map((e, i) => [e, i])) as Record<Ease, number>;
const SNAP_NORM = 1 / (1 - Math.pow(2, -10));

/** Apply easing `id` (index into EASES) at u in 0..1. */
export function easeAt(id: number, u: number, overshoot: number): number {
  if (u <= 0) return 0;
  if (u >= 1) return 1;
  switch (id) {
    case 1:
      return u * u * u;
    case 2: {
      const v = 1 - u;
      return 1 - v * v * v;
    }
    case 3:
      return u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2;
    case 4:
      return (1 - Math.pow(2, -10 * u)) * SNAP_NORM;
    case 5: {
      const v = u - 1;
      return 1 + (overshoot + 1) * v * v * v + overshoot * v * v;
    }
    case 6:
      return (overshoot + 1) * u * u * u - overshoot * u * u;
    case 7:
      return 0;
    default:
      return u;
  }
}

export function ease(e: Ease, u: number, overshoot = 1.70158): number {
  return easeAt(EASE_ID[e], u, overshoot);
}

// ---------------------------------------------------------------------------
// Authoring format
// ---------------------------------------------------------------------------

/** '#rrggbb' (sRGB, like a colour picker) or linear RGB 0..1. */
export type ColorDef = string | readonly [number, number, number];
export type RGB = readonly [number, number, number];

/** Up to three numbers per bone (missing = 0), see the channel table above. */
export type PoseDef = Partial<Record<ClipBone, readonly number[]>>;

export interface RootDef {
  /** Yards forward (visual only). */
  lunge?: number;
  /** Yards to the right (visual only). */
  side?: number;
  /** Yards up (visual only). */
  hop?: number;
  /** Turns about Y (1 = 360°, + = counter-clockwise from above). */
  spin?: number;
}

export interface KeyDef {
  /** Seconds from the clip start. */
  t: number;
  /** Easing of the segment arriving at this key (default 'inOut'). */
  ease?: Ease;
  /** Overshoot / pull-back amount for 'back' and 'anticipate' (default 1.7). */
  overshoot?: number;
  /** Seconds to hold this pose after reaching it (the "impact freeze"). */
  hold?: number;
  /** Bone offsets (missing bones = 0). */
  pose?: PoseDef;
  root?: RootDef;
}

/**
 * Ribbon texture styles:
 *  crescent  solid sweeping band with a bright leading edge (sword slashes)
 *  streak    thin parallel speed lines (fast thrusts, dashes)
 *  electric  flickering jagged bolt inside the band (storm / lightning)
 *  holy      soft wide glow with drifting sparkle bands (light / healing)
 */
export type TrailStyle = 'crescent' | 'streak' | 'electric' | 'holy';
export const TRAIL_STYLES: readonly TrailStyle[] = ['crescent', 'streak', 'electric', 'holy'];

export type HandDef = 'right' | 'left' | 'both';

export interface TrailDef {
  /** Window [from, to) in clip seconds: the ribbon samples the blade while open. */
  from: number;
  to: number;
  /** Which hand's weapon (default 'right'; 'both' = one ribbon per hand). Empty hands trail from the fingertips. */
  hand?: HandDef;
  /** Head colour. */
  color: ColorDef;
  /** Tail colour (default: a darker, more saturated head colour). */
  tail?: ColorDef;
  /** Fraction of the blade covered, measured from the tip (0..1, default 0.75). */
  width?: number;
  style?: TrailStyle;
  /** Seconds each point stays visible (default 0.2). */
  fade?: number;
  /** 'add' (default) glows; 'alpha' reads on bright floors (dark smears, wind). */
  blend?: 'add' | 'alpha';
  /** Peak opacity (default 0.7; scaled by the effects-intensity setting). */
  alpha?: number;
}

/** Effects a clip can fire at a moment (fx.ts `clipCue`). */
export type FxCueId = 'impact' | 'sparks' | 'slash' | 'shockwave' | 'flash' | 'dust' | 'holy' | 'electric' | 'embers' | 'cracks';
export const FX_CUES: readonly FxCueId[] = ['impact', 'sparks', 'slash', 'shockwave', 'flash', 'dust', 'holy', 'electric', 'embers', 'cracks'];

/** Where a cue fires: blade tip, the hand, ~1.3 yd in front at chest height, the ground in front, or the unit's chest. */
export type CueAt = 'tip' | 'hand' | 'front' | 'ground' | 'self';

export interface CueDef {
  t: number;
  fx: FxCueId;
  /** Default 'tip'. */
  at?: CueAt;
  /** Hand for 'tip' / 'hand' (default 'right'). */
  hand?: 'right' | 'left';
  /** Default: the clip's first trail colour. */
  color?: ColorDef;
  /** Size multiplier (default 1). */
  scale?: number;
}

export interface HitStopDef {
  t: number;
  /** Freeze length (ms, scaled by effects intensity; rate-limited). Only the local player's clips freeze the screen. */
  ms: number;
  /** Optional camera shake trauma (0..1, typical 0.1–0.35). */
  shake?: number;
}

export interface ClipDef {
  /** Debug name (shown in the clip browser). */
  id: string;
  /** Seconds. */
  duration: number;
  /** Loop [loopFrom, duration) until stopped (channels, cast loops). */
  loop?: boolean;
  /** Loop start (default 0). */
  loopFrom?: number;
  /** Crossfade in from the previous pose (default 0.06 s: presses must feel instant). */
  blendIn?: number;
  /** Fade over the last `blendOut` seconds (default 0.08 s). */
  blendOut?: number;
  /** Locomotion mask (default 'auto'). */
  mask?: 'auto' | 'upper' | 'full';
  /**
   * From this time on, a cast / channel loop may take over (chain casting). Default: the last
   * trail end, cue or hit-stop. A new ability press always takes over.
   */
  recoverAt?: number;
  keys: readonly KeyDef[];
  trails?: readonly TrailDef[];
  cues?: readonly CueDef[];
  hitStop?: HitStopDef | readonly HitStopDef[];
}

// ---------------------------------------------------------------------------
// Compiled form
// ---------------------------------------------------------------------------

export interface TrailWindow {
  from: number;
  to: number;
  /** 0 = right hand, 1 = left hand. */
  hand: 0 | 1;
  color: RGB;
  tail: RGB;
  width: number;
  style: TrailStyle;
  /** Index into TRAIL_STYLES. */
  styleId: number;
  fade: number;
  additive: boolean;
  alpha: number;
}

export interface CueMarker {
  t: number;
  fx: FxCueId;
  at: CueAt;
  hand: 0 | 1;
  color: RGB;
  scale: number;
}

export interface HitStopMarker {
  t: number;
  ms: number;
  shake: number;
}

export interface Clip {
  readonly id: string;
  readonly duration: number;
  readonly loop: boolean;
  readonly loopFrom: number;
  readonly blendIn: number;
  readonly blendOut: number;
  readonly mask: 'auto' | 'upper' | 'full';
  readonly recoverAt: number;
  /** Keys after hold expansion and implicit start/end keys. */
  readonly n: number;
  readonly times: Float32Array;
  readonly ease: Uint8Array;
  readonly overshoot: Float32Array;
  /** n × NCH offsets (spin in radians). */
  readonly values: Float32Array;
  /** Bit 1: animates the left arm, bit 2: the right arm (those arms drop their base gestures while it plays). */
  readonly armMask: number;
  readonly windows: readonly TrailWindow[];
  /** Sorted by time. */
  readonly cues: readonly CueMarker[];
  readonly stops: readonly HitStopMarker[];
  /** Primary colour (first trail, else neutral). */
  readonly color: RGB;
}

/** Most trail windows one clip may have (after 'both' expands). */
export const MAX_WINDOWS = 8;

const NEUTRAL: RGB = [0.9, 0.86, 0.78];

const srgbToLinear = (c: number): number => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));

export function toRGB(c: ColorDef): RGB {
  if (typeof c !== 'string') return [c[0], c[1], c[2]];
  const m = /^#?([0-9a-f]{6})$/i.exec(c.trim());
  if (!m) throw new Error(`clip colour "${c}" is not #rrggbb`);
  const v = parseInt(m[1]!, 16);
  return [srgbToLinear(((v >> 16) & 255) / 255), srgbToLinear(((v >> 8) & 255) / 255), srgbToLinear((v & 255) / 255)];
}

/** Default trail tail: darker and a little more saturated than the head. */
function tailOf(c: RGB): RGB {
  const l = (c[0] + c[1] + c[2]) / 3;
  const s = (x: number): number => Math.max(0, (l + (x - l) * 1.35) * 0.55);
  return [s(c[0]), s(c[1]), s(c[2])];
}

function writePose(k: KeyDef, out: Float32Array, o: number): void {
  if (k.pose) {
    for (const b of Object.keys(k.pose) as ClipBone[]) {
      const idx = BONE_INDEX[b];
      if (idx === undefined) throw new Error(`unknown clip bone "${b}"`);
      const v = k.pose[b]!;
      for (let a = 0; a < 3 && a < v.length; a++) out[o + idx * 3 + a] = v[a] ?? 0;
    }
  }
  if (k.root) {
    out[o + CH_LUNGE] = k.root.lunge ?? 0;
    out[o + CH_SIDE] = k.root.side ?? 0;
    out[o + CH_HOP] = k.root.hop ?? 0;
    out[o + CH_SPIN] = (k.root.spin ?? 0) * Math.PI * 2;
  }
}

/** Compile a clip definition (once, at module load). Throws on malformed data. */
export function defineClip(def: ClipDef): Clip {
  const where = `clip "${def.id}"`;
  if (!(def.duration > 0)) throw new Error(`${where}: duration must be > 0`);
  const loop = !!def.loop;
  const loopFrom = def.loopFrom ?? 0;
  if (loop && !(loopFrom >= 0 && loopFrom < def.duration - 1e-4)) throw new Error(`${where}: loopFrom must be in [0, duration)`);
  // Expand holds into duplicate keys.
  type K = { t: number; e: number; o: number; v: Float32Array };
  const ks: K[] = [];
  for (const k of def.keys) {
    const v = new Float32Array(NCH);
    writePose(k, v, 0);
    ks.push({ t: k.t, e: EASE_ID[k.ease ?? 'inOut'], o: k.overshoot ?? 1.70158, v });
    if (k.hold && k.hold > 0) ks.push({ t: k.t + k.hold, e: EASE_ID.linear, o: 0, v });
  }
  for (let i = 0; i < ks.length; i++) {
    const k = ks[i]!;
    if (!(k.t >= 0 && k.t <= def.duration + 1e-6)) throw new Error(`${where}: key at ${k.t}s is outside [0, ${def.duration}]`);
    if (i > 0 && !(k.t > ks[i - 1]!.t)) throw new Error(`${where}: key times must increase (key at ${k.t}s, holds included)`);
  }
  if (ks.length === 0 || ks[0]!.t > 0) ks.unshift({ t: 0, e: EASE_ID.linear, o: 0, v: new Float32Array(NCH) });
  if (ks[ks.length - 1]!.t < def.duration - 1e-6) {
    // Non-loops recover to the base pose; loops close back onto the loop-start pose.
    const end = new Float32Array(NCH);
    if (loop) evalKeys(ks, loopFrom, end);
    ks.push({ t: def.duration, e: EASE_ID.inOut, o: 0, v: end });
  }
  const n = ks.length;
  const times = new Float32Array(n);
  const easeIds = new Uint8Array(n);
  const over = new Float32Array(n);
  const values = new Float32Array(n * NCH);
  ks.forEach((k, i) => {
    times[i] = k.t;
    easeIds[i] = k.e;
    over[i] = k.o;
    values.set(k.v, i * NCH);
  });
  let armMask = 0;
  for (let i = 0; i < n; i++) {
    for (const b of LEFT_ARM) for (let a = 0; a < 3; a++) if (values[i * NCH + BONE_INDEX[b] * 3 + a] !== 0) armMask |= 1;
    for (const b of RIGHT_ARM) for (let a = 0; a < 3; a++) if (values[i * NCH + BONE_INDEX[b] * 3 + a] !== 0) armMask |= 2;
  }

  const windows: TrailWindow[] = [];
  for (const w of def.trails ?? []) {
    if (!(w.to > w.from)) throw new Error(`${where}: trail window [${w.from}, ${w.to}) is empty`);
    const color = toRGB(w.color);
    const style = w.style ?? 'crescent';
    if (!TRAIL_STYLES.includes(style)) throw new Error(`${where}: unknown trail style "${style}"`);
    const base = {
      from: w.from,
      to: w.to,
      color,
      tail: w.tail ? toRGB(w.tail) : tailOf(color),
      width: Math.min(1, Math.max(0.05, w.width ?? 0.75)),
      style,
      styleId: TRAIL_STYLES.indexOf(style),
      fade: Math.max(0.04, w.fade ?? 0.2),
      additive: (w.blend ?? 'add') === 'add',
      alpha: Math.min(1, Math.max(0, w.alpha ?? 0.7)),
    };
    const hand = w.hand ?? 'right';
    if (hand === 'right' || hand === 'both') windows.push({ ...base, hand: 0 });
    if (hand === 'left' || hand === 'both') windows.push({ ...base, hand: 1 });
  }
  if (windows.length > MAX_WINDOWS) throw new Error(`${where}: at most ${MAX_WINDOWS} trail windows`);
  const color = windows[0]?.color ?? NEUTRAL;
  const cues: CueMarker[] = (def.cues ?? []).map((c) => {
    if (!FX_CUES.includes(c.fx)) throw new Error(`${where}: unknown fx cue "${c.fx}"`);
    return { t: c.t, fx: c.fx, at: c.at ?? 'tip', hand: c.hand === 'left' ? 1 : 0, color: c.color ? toRGB(c.color) : color, scale: c.scale ?? 1 };
  });
  cues.sort((a, b) => a.t - b.t);
  const hs = def.hitStop ? (Array.isArray(def.hitStop) ? def.hitStop : [def.hitStop]) as readonly HitStopDef[] : [];
  const stops: HitStopMarker[] = hs.map((h) => ({ t: h.t, ms: h.ms, shake: h.shake ?? 0 })).sort((a, b) => a.t - b.t);
  for (const m of [...cues, ...stops]) if (!(m.t >= 0 && m.t <= def.duration)) throw new Error(`${where}: marker at ${m.t}s is outside the clip`);

  let recoverAt = def.recoverAt ?? -1;
  if (recoverAt < 0) {
    for (const w of windows) recoverAt = Math.max(recoverAt, w.to);
    for (const c of cues) recoverAt = Math.max(recoverAt, c.t);
    for (const s of stops) recoverAt = Math.max(recoverAt, s.t);
    if (recoverAt < 0) recoverAt = def.duration * 0.6;
  }
  return {
    id: def.id,
    duration: def.duration,
    loop,
    loopFrom,
    blendIn: Math.max(0, def.blendIn ?? 0.06),
    blendOut: Math.max(0, def.blendOut ?? 0.08),
    mask: def.mask ?? 'auto',
    recoverAt,
    n,
    times,
    ease: easeIds,
    overshoot: over,
    values,
    armMask,
    windows,
    cues,
    stops,
    color,
  };
}

function evalKeys(ks: { t: number; e: number; o: number; v: Float32Array }[], t: number, out: Float32Array): void {
  let i = 0;
  while (i < ks.length - 1 && ks[i + 1]!.t <= t) i++;
  const a = ks[i]!;
  const b = ks[Math.min(ks.length - 1, i + 1)]!;
  const u = b.t > a.t ? Math.min(1, Math.max(0, (t - a.t) / (b.t - a.t))) : 0;
  const e = easeAt(b.e, u, b.o);
  for (let c = 0; c < NCH; c++) out[c] = a.v[c]! + (b.v[c]! - a.v[c]!) * e;
}

/** The clip's pose offsets at local time `t` (seconds, clamped) into `out` (returned). No allocation. */
export function evalClip(clip: Clip, t: number, out: Float32Array): Float32Array {
  const T = clip.times;
  const V = clip.values;
  const n = clip.n;
  if (t <= T[0]!) {
    for (let c = 0; c < NCH; c++) out[c] = V[c]!;
    return out;
  }
  if (t >= T[n - 1]!) {
    const o = (n - 1) * NCH;
    for (let c = 0; c < NCH; c++) out[c] = V[o + c]!;
    return out;
  }
  let i = 0;
  while (T[i + 1]! <= t) i++;
  const u = (t - T[i]!) / (T[i + 1]! - T[i]!);
  const e = easeAt(clip.ease[i + 1]!, u, clip.overshoot[i + 1]!);
  const a = i * NCH;
  const b = a + NCH;
  for (let c = 0; c < NCH; c++) {
    const va = V[a + c]!;
    out[c] = va + (V[b + c]! - va) * e;
  }
  return out;
}

/** Local clip time for `elapsed` seconds since the start (loops wrap into [loopFrom, duration)). */
export function localTime(clip: Clip, elapsed: number): number {
  if (elapsed < clip.duration) return Math.max(0, elapsed);
  if (!clip.loop) return clip.duration;
  const L = clip.duration - clip.loopFrom;
  return clip.loopFrom + ((elapsed - clip.duration) % L);
}

/** Is trail window `w` open at local time `t`? */
export function windowOpen(w: TrailWindow, t: number): boolean {
  return t >= w.from && t < w.to;
}

export const wrapPi = (a: number): number => {
  if (a > -Math.PI && a <= Math.PI) return a;
  const x = (a + Math.PI) % (Math.PI * 2);
  return (x <= 0 ? x + Math.PI * 2 : x) - Math.PI;
};

const smooth = (x: number): number => (x <= 0 ? 0 : x >= 1 ? 1 : x * x * (3 - 2 * x));

// ---------------------------------------------------------------------------
// Runtime player
// ---------------------------------------------------------------------------

export type ClipPhase = 'strike' | 'cast' | 'channel';

/** What a playing clip asks of its owner (the hero view). Implementations must not allocate. */
export interface ClipSink {
  fxCue(cue: CueMarker): void;
  hitStop(m: HitStopMarker): void;
  /** Trail window `i` of the current clip opened: start a ribbon. */
  trailStart(i: number, w: TrailWindow): void;
  /** Trail window `i` closed (or the clip stopped): let the ribbon fade. */
  trailEnd(i: number): void;
}

/** Fade used when a predicted press is rejected or a stun / interrupt cuts a clip. */
export const CANCEL_FADE = 0.14;

/**
 * Plays one clip at a time with crossfades. Output: `out` (pose offsets, already weighted and
 * masked; spin wrapped to ±π so blending never unwinds a full turn) and `armL` / `armR` (0..1:
 * how much each arm's base gestures, like the guard, staff hold or cast pose, give way to the clip).
 */
export class ClipPlayer {
  clip: Clip | null = null;
  abilityId: string | null = null;
  phase: ClipPhase | null = null;
  /** Local clip time (wrapped for loops). */
  t = 0;
  /** Seconds since `play` (not wrapped). */
  elapsed = 0;
  readonly out = new Float32Array(NCH);
  armL = 0;
  armR = 0;
  /** Weight of the live clip (0..1) this frame. */
  weight = 0;
  /** Bit i set while trail window i is open. */
  readonly open = new Uint8Array(MAX_WINDOWS);
  private readonly live = new Float32Array(NCH);
  private readonly snap = new Float32Array(NCH);
  private snapArmL = 0;
  private snapArmR = 0;
  private snapAge = 0;
  private snapDur = 0;

  /** Anything still moving (a clip or a fading snapshot)? */
  get active(): boolean {
    return this.clip !== null || this.snapDur > 0;
  }

  /** Past the clip's strike (a cast loop may take over)? True when idle. */
  get recovering(): boolean {
    const c = this.clip;
    return !c || c.loop || this.t >= c.recoverAt;
  }

  /** Start `clip`, crossfading from the current output over its blendIn. */
  play(clip: Clip, abilityId: string | null, phase: ClipPhase, sink: ClipSink): void {
    this.closeTrails(sink);
    this.capture(clip.blendIn);
    this.clip = clip;
    this.abilityId = abilityId;
    this.phase = phase;
    this.t = 0;
    this.elapsed = 0;
    this.firstFrame = true;
  }

  /** Blend out from the current output over `fade` seconds. */
  stop(fade: number, sink: ClipSink): void {
    if (!this.clip) return;
    this.closeTrails(sink);
    this.capture(fade);
    this.clip = null;
    this.abilityId = null;
    this.phase = null;
  }

  /** Rejected press / interrupt: quick fade, trails released, no further cues. */
  cancel(sink: ClipSink): void {
    this.stop(CANCEL_FADE, sink);
  }

  /**
   * A predicted press for `abilityId` was rejected: cancel its strike if that is what plays
   * (a later press or a cast loop that took over is left alone). Returns true if cancelled.
   */
  cancelIf(abilityId: string | null, sink: ClipSink): boolean {
    if (!this.clip || this.phase !== 'strike' || this.abilityId !== abilityId) return false;
    this.cancel(sink);
    return true;
  }

  /** Drop everything at once (teleport, dispose). */
  reset(sink: ClipSink): void {
    this.closeTrails(sink);
    this.clip = null;
    this.abilityId = null;
    this.phase = null;
    this.snapDur = 0;
    this.out.fill(0);
    this.armL = this.armR = this.weight = 0;
  }

  private firstFrame = false;

  private capture(dur: number): void {
    this.snap.set(this.out);
    this.snap[CH_SPIN] = wrapPi(this.snap[CH_SPIN]!);
    // Hand rolls (staff / blade twirls) too: a twirl cut mid-turn (interrupt, stun, a new press)
    // fades out the short way instead of spinning back through every turn it made.
    this.snap[CH_ROLL_R] = wrapPi(this.snap[CH_ROLL_R]!);
    this.snap[CH_ROLL_L] = wrapPi(this.snap[CH_ROLL_L]!);
    this.snapArmL = this.armL;
    this.snapArmR = this.armR;
    this.snapAge = 0;
    this.snapDur = Math.max(1e-3, dur);
  }

  private closeTrails(sink: ClipSink): void {
    for (let i = 0; i < MAX_WINDOWS; i++) {
      if (this.open[i]) {
        this.open[i] = 0;
        sink.trailEnd(i);
      }
    }
  }

  /** Fire cues and hit-stops with a < t ≤ b (local time; `cycle`: inside a loop repeat). */
  private fire(clip: Clip, a: number, b: number, cycle: boolean, sink: ClipSink): void {
    const L = clip.duration - clip.loopFrom;
    for (let i = 0; i < clip.cues.length; i++) {
      const c = clip.cues[i]!;
      let tc = c.t;
      if (cycle) {
        if (tc < clip.loopFrom - 1e-6) continue;
        if (tc <= clip.loopFrom + 1e-6) tc += L; // a cue on the loop start fires as each repeat begins
      }
      if (tc > a && tc <= b) sink.fxCue(c);
    }
    for (let i = 0; i < clip.stops.length; i++) {
      const s = clip.stops[i]!;
      let ts = s.t;
      if (cycle) {
        if (ts < clip.loopFrom - 1e-6) continue;
        if (ts <= clip.loopFrom + 1e-6) ts += L;
      }
      if (ts > a && ts <= b) sink.hitStop(s);
    }
  }

  /** Fire every marker crossed between `prev` and `now` (seconds since play). */
  private fireSpan(clip: Clip, prev: number, now: number, sink: ClipSink): void {
    let a = prev;
    if (a < clip.duration) {
      this.fire(clip, a, Math.min(now, clip.duration), false, sink);
      if (!clip.loop || now <= clip.duration) return;
      a = clip.duration;
    }
    const L = clip.duration - clip.loopFrom;
    // Walk loop repeats (bounded: a long hitch skips repeats instead of spinning here).
    for (let guard = 0; guard < 4 && a < now; guard++) {
      const la = clip.loopFrom + ((a - clip.duration) % L);
      const span = Math.min(now - a, clip.duration - la);
      this.fire(clip, la, la + span, true, sink);
      a += span;
      if (span <= 1e-9) break;
    }
  }

  /** Advance by `dt` seconds. `run` 0..1 is the locomotion blend (for the 'auto' mask). */
  update(dt: number, run: number, sink: ClipSink): void {
    const out = this.out;
    let sk = 0;
    if (this.snapDur > 0) {
      this.snapAge += dt;
      const x = this.snapAge / this.snapDur;
      if (x >= 1) this.snapDur = 0;
      else sk = 1 - smooth(x);
    }
    let lk = 0;
    const clip = this.clip;
    if (clip) {
      // The first update after `play` also fires markers at exactly t = 0.
      const prev = this.firstFrame ? -1e-6 : this.elapsed;
      this.elapsed += dt;
      this.firstFrame = false;
      this.fireSpan(clip, prev, this.elapsed, sink);
      if (!clip.loop && this.elapsed >= clip.duration) {
        // Finished: the last key is (normally) the base pose and blendOut has faded it already.
        this.closeTrails(sink);
        this.clip = null;
        this.abilityId = null;
        this.phase = null;
        this.t = clip.duration;
      } else {
        const t = localTime(clip, this.elapsed);
        this.t = t;
        // Trail windows.
        const W = clip.windows;
        for (let i = 0; i < W.length; i++) {
          const on = windowOpen(W[i]!, t);
          if (on && !this.open[i]) {
            this.open[i] = 1;
            sink.trailStart(i, W[i]!);
          } else if (!on && this.open[i]) {
            this.open[i] = 0;
            sink.trailEnd(i);
          }
        }
        evalClip(clip, t, this.live);
        const live = this.live;
        live[CH_SPIN] = wrapPi(live[CH_SPIN]!);
        const lower = clip.mask === 'full' ? 1 : clip.mask === 'upper' ? 0 : 1 - Math.min(1, Math.max(0, run));
        if (lower < 1) for (let c = 0; c < NCH; c++) if (LOWER_BODY[c]) live[c] = live[c]! * lower;
        let env = 1;
        if (!clip.loop && clip.blendOut > 0) env = smooth((clip.duration - t) / clip.blendOut);
        lk = (1 - sk) * env;
      }
    }
    if (lk > 0) {
      const live = this.live;
      const snap = this.snap;
      for (let c = 0; c < NCH; c++) out[c] = snap[c]! * sk + live[c]! * lk;
    } else if (sk > 0) {
      const snap = this.snap;
      for (let c = 0; c < NCH; c++) out[c] = snap[c]! * sk;
    } else out.fill(0);
    const m = lk > 0 && this.clip ? this.clip.armMask : 0;
    this.armL = Math.min(1, this.snapArmL * sk + (m & 1 ? lk : 0));
    this.armR = Math.min(1, this.snapArmR * sk + (m & 2 ? lk : 0));
    this.weight = lk;
  }
}
